import React, { useState, useEffect } from 'react';
import { chatAPI, resolveBaseURL } from '../services/api';
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Badge,
  IconButton,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  AlertTitle,
  Button
} from '@mui/material';
import {
  Search,
  MoreVert,
  Circle,
  Videocam,
  Phone,
  Info,
  Edit,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNavigation from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import useResponsive from '../hooks/useResponsive';
import { formatDateShort, getCurrentDateInTimezone } from '../utils/timezoneUtils';

const Chat = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { 
    isMobile: isResponsiveMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  

  // Fetch chats from API
  const fetchChats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use direct fetch to bypass broken API import
      const token = localStorage.getItem('customerToken') || localStorage.getItem('token');
      
      const directResponse = await fetch(`${resolveBaseURL()}/customer/chat?t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      
      if (!directResponse.ok) {
        const errorText = await directResponse.text();
        throw new Error(`HTTP error! status: ${directResponse.status}, message: ${errorText}`);
      }
      
      const responseData = await directResponse.json();
      
      const response = {
        success: responseData.success,
        data: {
          chats: responseData.data?.chats || []
        }
      };
      
      
      if (response.success) {
        setChats(response.data.chats || []);
      } else {
        setError(responseData.message || 'Failed to load chats');
      }
    } catch (error) {
      setError('Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  // Fetch chats when component mounts
  useEffect(() => {
    fetchChats();
  }, []);

  // Refresh chat count when user returns to this page
  useEffect(() => {
    const handleFocus = () => {
      if (window.refreshChatCount) {
        window.refreshChatCount();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    // Convert timestamp to Date object if it's not already
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) return 'Invalid date';
    
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return formatDateShort(date);
  };

  const filteredChats = chats.filter(chat =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChatClick = (chat) => {
    // Navigate using provider ID, not chat ID
    navigate(`/chat/${chat.providerId}`, {
      state: {
        provider: {
          id: chat.providerId,
          name: chat.name,
          avatar: chat.avatar,
          actualChatId: chat.id // Pass the actual chat ID for reference
        }
      }
    });
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#0A2A66' }}>
        <Toolbar>
                     <IconButton 
             color="inherit" 
             onClick={() => navigate('/home')}
             sx={{ mr: 2 }}
           >
             <ArrowBack />
           </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Chats
          </Typography>
          <IconButton color="inherit" size="small">
            <Edit />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Search Bar */}
      <Box sx={{ p: 2, backgroundColor: '#f0f0f0' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search or start new chat"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{
            backgroundColor: 'white',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          flex: 1,
          flexDirection: 'column',
          gap: 2
        }}>
          <BrandLoader />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          flex: 1,
          flexDirection: 'column',
          gap: 2
        }}>
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
            <Button 
              onClick={fetchChats} 
              sx={{ mt: 1 }}
              variant="outlined"
            >
              Try Again
            </Button>
          </Alert>
        </Box>
      )}

      {/* Chat List - Only show when not loading */}
      {!loading && !error && (
        <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: 'white' }}>
          <List sx={{ p: 0 }}>
            {filteredChats.map((chat, index) => (
            <React.Fragment key={chat.id}>
              <ListItem
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease-in-out',
                  },
                  py: 2,
                  px: 3,
                  borderLeft: '4px solid transparent',
                  '&:hover': {
                    borderLeft: '4px solid #0A2A66',
                  },
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                  borderBottom: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={() => handleChatClick(chat)}
              >
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      chat.isOnline ? (
                        <Circle sx={{ fontSize: 14, color: '#25D366' }} />
                      ) : null
                    }
                  >
                    <Avatar
                      src={chat.photoURL || chat.profilePhotoUrl || chat.profilePicture || chat.logoUrl || chat.businessImage || chat.image || chat.avatar || ''}
                      sx={{ 
                        width: 56, 
                        height: 56,
                        border: '3px solid #e0e0e0',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      {/* Fallback: Show first letter of business name if no image */}
                      {!chat.photoURL && !chat.profilePhotoUrl && !chat.profilePicture && !chat.logoUrl && !chat.businessImage && !chat.image && !chat.avatar && chat.name ? 
                        chat.name.charAt(0).toUpperCase() : null
                      }
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ 
                          fontWeight: 700, 
                          color: '#0A2A66',
                          fontSize: '1rem'
                        }}>
                          {chat.name}
                        </Typography>
                        {chat.businessType && (
                          <Chip 
                            label={chat.businessType} 
                            size="small" 
                            sx={{ 
                              fontSize: '0.65rem',
                              height: '20px',
                              backgroundColor: '#E3F2FD',
                              color: '#1976D2',
                              fontWeight: 500,
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        )}
                        {chat.isVerified && (
                          <Chip 
                            label="✓" 
                            size="small" 
                            sx={{ 
                              fontSize: '0.7rem',
                              height: '18px',
                              backgroundColor: '#25D366',
                              color: 'white',
                              '& .MuiChip-label': {
                                px: 0.5
                              }
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.75rem',
                        color: '#666',
                        fontWeight: 500
                      }}>
                        {formatTimestamp(chat.timestamp)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        {chat.isTyping ? (
                          <Chip 
                            label="typing..." 
                            size="small" 
                            sx={{ 
                              fontSize: '0.75rem',
                              backgroundColor: '#E8F5E8',
                              color: '#075E54'
                            }}
                          />
                        ) : (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: isMobile ? '200px' : '300px',
                              fontSize: '0.875rem',
                              color: chat.unreadCount > 0 ? '#333' : '#666',
                              fontWeight: chat.unreadCount > 0 ? 500 : 400
                            }}
                          >
                            {chat.lastMessage}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {chat.unreadCount > 0 && (
                          <Badge
                            badgeContent={chat.unreadCount}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: '#FF4444',
                                color: 'white',
                                fontSize: '0.7rem',
                                minWidth: '18px',
                                height: '18px',
                                borderRadius: '9px',
                                fontWeight: 600,
                                boxShadow: '0 2px 4px rgba(255,68,68,0.3)',
                              }
                            }}
                          />
                        )}
                        <IconButton size="small" sx={{ opacity: 0.6 }}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
              {index < filteredChats.length - 1 && (
                <Divider variant="inset" component="li" />
              )}
            </React.Fragment>
          ))}
        </List>
        </Box>
      )}

      {/* Empty State */}
      {!loading && !error && filteredChats.length === 0 && (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 4,
          backgroundColor: 'white'
        }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No conversations found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {searchQuery ? 'Try adjusting your search terms' : 'Start a new conversation to begin chatting'}
          </Typography>
        </Box>
      )}

      {/* Bottom Navigation */}
      <BottomNavigation />

    </Box>
  );
};

export default Chat;
